import { useEffect, useMemo, useState } from 'react';

import { filterChainByPaths } from 'modules/chains/utils/filterChainByPaths';
import {
  selectBlockchainBySubchainId,
  selectPrivateChainById,
  selectSubChainIdByPath,
} from 'modules/chains/store/selectors';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAppSelector } from 'store/useAppSelector';
import { useFetchProjectsWhitelistsBlockchainsQuery } from 'domains/projects/actions/fetchProjectsWhitelistsBlockchains';
import { useJWTs } from 'domains/jwtToken/hooks/useJWTs';
import { usePartnerClientStatus } from 'domains/jwtToken/hooks/usePartnerClientStatus';
import { useTokenManagerConfigSelector } from 'domains/jwtToken/hooks/useTokenManagerConfigSelector';

import { ALL_CHAINS_VALUE } from './constants';
import { ChainContext, ChainContextValue } from './context';

interface ChainProviderProps {
  children: React.ReactNode;
}

export const ChainProvider = ({ children }: ChainProviderProps) => {
  const { group, isAccountInitialized, userEndpointToken, workerToken } =
    useAccount();

  const { isPartnerClient, partnerChains } = usePartnerClientStatus();

  const {
    jwts: projects,
    loading: projectsLoading,
    state: { isSuccess: projectsLoaded },
  } = useJWTs({ group });

  const { isLoading: projectsWhitelistsLoading } =
    useFetchProjectsWhitelistsBlockchainsQuery(
      {
        group,
        projects,
      },
      {
        skip: projectsLoading || !projectsLoaded || !isAccountInitialized,
      },
    );

  const partnerChainID = useAppSelector(state =>
    selectSubChainIdByPath(state, partnerChains?.[0]),
  );

  const partnerMainChainID = useAppSelector(state =>
    selectBlockchainBySubchainId(state, partnerChainID),
  )?.id;

  const initialValue = partnerMainChainID || ALL_CHAINS_VALUE;

  const [selectedChainID, setSelectedChainID] = useState(initialValue);

  useEffect(() => {
    setSelectedChainID(initialValue);
    // Only group change should be tracked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  useEffect(() => {
    if (!partnerChainID) {
      setSelectedChainID(ALL_CHAINS_VALUE);
    }
  }, [partnerChainID, workerToken]);

  const mainChainID =
    isPartnerClient &&
    partnerMainChainID &&
    selectedChainID === ALL_CHAINS_VALUE
      ? partnerMainChainID
      : selectedChainID;

  const { selectedProjectEndpointToken = userEndpointToken } =
    useTokenManagerConfigSelector();

  const mainChain = useAppSelector(state =>
    selectPrivateChainById(state, mainChainID, selectedProjectEndpointToken),
  );

  const chain = useMemo(
    () =>
      isPartnerClient && mainChain && partnerChains
        ? filterChainByPaths({ chain: mainChain, paths: partnerChains })
        : mainChain,
    [isPartnerClient, mainChain, partnerChains],
  );

  const loading = projectsWhitelistsLoading || projectsLoading;

  const value = useMemo(
    (): ChainContextValue => ({
      chain,
      loading,
      partnerMainChainID,
      selectedChainID,
      setSelectedChainID,
    }),
    [chain, loading, partnerMainChainID, selectedChainID],
  );

  return (
    <ChainContext.Provider value={value}>{children}</ChainContext.Provider>
  );
};
